import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { fetchLmsResourceFileBlob, lmsAPI } from '../../services/api';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Chip,
  TextField,
  Button,
  MenuItem,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Drawer,
  ListItemButton,
  ListItemIcon,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Badge,
  Tabs,
  Tab,
  Link,
  Stack,
} from '@mui/material';
import PageBackButton from '../../components/PageBackButton';
import LmsEmbedFrame from '../../components/LmsEmbedFrame';
import { getEmbeddableUrl } from '../../utils/lmsEmbedUrl';
import { toast } from 'react-hot-toast';
import {
  Edit,
  Delete,
  Search,
  ExpandLess,
  ExpandMore,
  MenuBook,
  Inventory,
  CalendarToday,
  VideoLibrary,
  Add,
  Layers,
  PlayArrow,
  Close,
  ArrowBack,
} from '@mui/icons-material';

const DRAWER_WIDTH = 320;

// Display label for type (Month -> Activity in UI only)
const typeLabel = (type) => {
  if (type === 'month') return 'Activity';
  if (type === 'resource') return 'Learning Resource';
  return (type || '').charAt(0).toUpperCase() + (type || '').slice(1);
};

const RESOURCE_TYPE_OPTIONS = [
  { value: 'video', label: 'Video' },
  { value: 'pdf', label: 'PDF' },
  { value: 'image', label: 'Image' },
  { value: 'worksheet', label: 'Worksheet' },
];

const findParentItem = (item, { allCourses, modules, months }) => {
  if (!item) return null;
  if (item.type === 'resource' || item.type === 'video') {
    const parent = Object.values(months)
      .flat()
      .find((month) => month.id === item.month_id);
    return parent ? { ...parent, type: 'month' } : null;
  }
  if (item.type === 'month') {
    const parent = Object.values(modules)
      .flat()
      .find((mod) => mod.id === item.module_id);
    return parent ? { ...parent, type: 'module' } : null;
  }
  if (item.type === 'module') {
    const parent = allCourses.find((course) => course.id === item.course_id);
    return parent ? { ...parent, type: 'course' } : null;
  }
  return null;
};

const getHierarchyBackLabel = (parent) => {
  if (!parent) return 'Back to overview';
  const name = parent.name || parent.title;
  return `Back to ${typeLabel(parent.type)}: ${name}`;
};

const CONTENT_TREE_QUERY_KEY = ['lms-content-tree'];

const lmsApiErrorMessage = (error, fallback = 'Request failed') => {
  const data = error?.response?.data;
  if (Array.isArray(data?.errors) && data.errors.length) return data.errors.join(', ');
  return data?.message || error?.message || fallback;
};

const LMSContent = () => {
  const { data: treeResponse, isLoading: loadingCourses } = useQuery(
    CONTENT_TREE_QUERY_KEY,
    () => lmsAPI.getContentTree().then((res) => res.data)
  );
  const queryClient = useQueryClient();
  const treeData = treeResponse?.data || {};
  const allCourses = treeData.courses || [];
  const defaultProgramId = (treeData.programs || [])[0]?.id;
  const modules = treeData.modules || {};
  const months = treeData.months || {};
  const resources = treeData.resources || treeData.videos || {};
  const videos = resources;
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [activeTab, setActiveTab] = useState(0); // 0 = Structure, 1 = Content
  const [createDialog, setCreateDialog] = useState({ open: false, type: '', parentId: null });
  const [editDialog, setEditDialog] = useState({ open: false, type: '', entity: null });
  const [formData, setFormData] = useState({});
  const [videoPlayerOpen, setVideoPlayerOpen] = useState(false);
  const [videoUrl, setVideoUrl] = useState('');
  const [fileViewer, setFileViewer] = useState(null);
  const [fileViewerLoading, setFileViewerLoading] = useState(false);

  const closeFileViewer = () => {
    setFileViewer((current) => {
      if (current?.url) URL.revokeObjectURL(current.url);
      return null;
    });
  };

  const openResourceFile = async (resource) => {
    if (!resource?.id || fileViewerLoading) return;
    setFileViewerLoading(true);
    try {
      const blob = await fetchLmsResourceFileBlob(resource.id);
      const url = URL.createObjectURL(blob);
      setFileViewer((current) => {
        if (current?.url) URL.revokeObjectURL(current.url);
        return {
          url,
          title: resource.title || resource.name || 'File',
          isImage: String(blob.type || '').startsWith('image/'),
        };
      });
    } catch (error) {
      toast.error(error.message || 'Could not open file');
    } finally {
      setFileViewerLoading(false);
    }
  };

  // Mutations
  const createMutations = {
    program: useMutation((payload) => lmsAPI.createProgram(payload), {
    onSuccess: () => {
      toast.success('Program created');
      queryClient.invalidateQueries(CONTENT_TREE_QUERY_KEY);
        setCreateDialog({ open: false, type: '', parentId: null });
        setFormData({});
      },
    }),
    course: useMutation((payload) => lmsAPI.createCourse(payload), {
    onSuccess: async () => {
      toast.success('Course created');
      queryClient.invalidateQueries(CONTENT_TREE_QUERY_KEY);
        setCreateDialog({ open: false, type: '', parentId: null });
        setFormData({});
      },
    }),
    module: useMutation((payload) => lmsAPI.createModule(payload), {
    onSuccess: async () => {
      toast.success('Module created');
      queryClient.invalidateQueries(CONTENT_TREE_QUERY_KEY);
        setCreateDialog({ open: false, type: '', parentId: null });
        setFormData({});
      },
    }),
    month: useMutation((payload) => lmsAPI.createMonth(payload), {
    onSuccess: async () => {
      toast.success('Activity created');
      queryClient.invalidateQueries(CONTENT_TREE_QUERY_KEY);
        setCreateDialog({ open: false, type: '', parentId: null });
        setFormData({});
      },
    }),
    resource: useMutation((payload) => lmsAPI.createResource(payload), {
      onSuccess: async () => {
        toast.success('Learning resource created');
        queryClient.invalidateQueries(CONTENT_TREE_QUERY_KEY);
        setCreateDialog({ open: false, type: '', parentId: null });
        setFormData({});
      },
    }),
  };

  const closeEditDialog = () => {
    setEditDialog({ open: false, type: '', entity: null });
    setFormData({});
  };

  const editMutationMap = {
    program: useMutation(({ id, payload }) => lmsAPI.updateProgram(id, payload), {
      onSuccess: () => {
        toast.success('Program updated');
        queryClient.invalidateQueries(CONTENT_TREE_QUERY_KEY);
        closeEditDialog();
      },
      onError: (error) => toast.error(lmsApiErrorMessage(error, 'Could not update program')),
    }),
    course: useMutation(({ id, payload }) => lmsAPI.updateCourse(id, payload), {
      onSuccess: async () => {
        toast.success('Course updated');
        queryClient.invalidateQueries(CONTENT_TREE_QUERY_KEY);
        closeEditDialog();
      },
      onError: (error) => toast.error(lmsApiErrorMessage(error, 'Could not update course')),
    }),
    module: useMutation(({ id, payload }) => lmsAPI.updateModule(id, payload), {
      onSuccess: async () => {
        toast.success('Module updated');
        queryClient.invalidateQueries(CONTENT_TREE_QUERY_KEY);
        closeEditDialog();
      },
      onError: (error) => toast.error(lmsApiErrorMessage(error, 'Could not update module')),
    }),
    month: useMutation(({ id, payload }) => lmsAPI.updateMonth(id, payload), {
      onSuccess: async () => {
        toast.success('Activity updated');
        queryClient.invalidateQueries(CONTENT_TREE_QUERY_KEY);
        closeEditDialog();
      },
      onError: (error) => toast.error(lmsApiErrorMessage(error, 'Could not update activity')),
    }),
    resource: useMutation(({ id, payload }) => lmsAPI.updateResource(id, payload), {
      onSuccess: async () => {
        toast.success('Learning resource updated');
        queryClient.invalidateQueries(CONTENT_TREE_QUERY_KEY);
        closeEditDialog();
      },
      onError: (error) => toast.error(lmsApiErrorMessage(error, 'Could not update learning resource')),
    }),
  };

  const deleteMutationMap = {
    program: useMutation((id) => lmsAPI.deleteProgram(id), {
      onSuccess: () => {
        toast.success('Program deleted');
        queryClient.invalidateQueries(CONTENT_TREE_QUERY_KEY);
        setSelectedItem(null);
      },
    }),
    course: useMutation((id) => lmsAPI.deleteCourse(id), {
      onSuccess: async () => {
        toast.success('Course deleted');
        queryClient.invalidateQueries(CONTENT_TREE_QUERY_KEY);
        setSelectedItem(null);
      },
    }),
    module: useMutation((id) => lmsAPI.deleteModule(id), {
      onSuccess: async () => {
        toast.success('Module deleted');
        queryClient.invalidateQueries(CONTENT_TREE_QUERY_KEY);
        setSelectedItem(null);
      },
    }),
    month: useMutation((id) => lmsAPI.deleteMonth(id), {
      onSuccess: async () => {
        toast.success('Activity deleted');
        queryClient.invalidateQueries(CONTENT_TREE_QUERY_KEY);
        setSelectedItem(null);
      },
    }),
    resource: useMutation((id) => lmsAPI.deleteResource(id), {
      onSuccess: async () => {
        toast.success('Learning resource deleted');
        queryClient.invalidateQueries(CONTENT_TREE_QUERY_KEY);
        setSelectedItem(null);
      },
    }),
  };

  const handleToggleExpand = (itemId) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(itemId)) {
      newExpanded.delete(itemId);
    } else {
      newExpanded.add(itemId);
    }
    setExpandedItems(newExpanded);
  };

  const handleSelectItem = (item, type) => {
    setSelectedItem({ ...item, type });
  };

  const handleCreate = (type, parentId = null) => {
    setCreateDialog({ open: true, type, parentId });
    setFormData({});
  };

  const handleEdit = (item, type) => {
    setEditDialog({ open: true, type, entity: item });
    const displayName = item.title || item.name || '';
    setFormData({
      name: type === 'resource' ? '' : displayName,
      title: type === 'resource' ? displayName : '',
      description: item.description || '',
      youtube_url: item.youtube_url || item.source_url || '',
      video_type: item.video_type || item.audience || 'teacher_training',
      resource_type: item.resource_type || 'video',
      audience: item.audience || item.video_type || 'teacher_training',
    });
  };

  const handleDelete = (id, type) => {
    if (window.confirm(`Are you sure you want to delete this ${typeLabel(type)}?`)) {
      deleteMutationMap[type].mutate(id);
    }
  };

  const handleCreateSubmit = () => {
    const mutation = createMutations[createDialog.type];
    if (!mutation) return;

    const payload = { description: formData.description || '' };
    
    if (createDialog.type === 'resource') {
      const resourceType = formData.resource_type || 'video';
      if (resourceType !== 'video') {
        if (!formData.file) {
          toast.error('Please select a file to upload');
          return;
        }
        const fd = new FormData();
        fd.append('activity_id', createDialog.parentId);
        fd.append('title', formData.title || formData.name || '');
        fd.append('description', formData.description || '');
        fd.append('resource_type', resourceType);
        fd.append('audience', formData.audience || formData.video_type || 'teacher_training');
        fd.append('file', formData.file);
        createMutations.resource.mutate(fd);
        return;
      }
      payload.title = formData.title || formData.name || '';
      payload.resource_type = 'video';
      payload.youtube_url = formData.youtube_url || '';
      payload.audience = formData.audience || formData.video_type || 'teacher_training';
      payload.activity_id = createDialog.parentId;
    } else {
      payload.name = formData.name || formData.title || '';
      if (createDialog.type === 'course') payload.program_id = createDialog.parentId;
      else if (createDialog.type === 'module') payload.course_id = createDialog.parentId;
      else if (createDialog.type === 'month') payload.module_id = createDialog.parentId;
    }

    mutation.mutate(payload);
  };

  const handleEditSubmit = () => {
    const { type, entity } = editDialog;
    if (!type || !entity) return;

    const description = formData.description ?? entity.description ?? '';
    
    if (type === 'resource') {
      const resourceType = entity.resource_type || formData.resource_type || 'video';
      const title = formData.title ?? entity.title ?? entity.name ?? '';
      const audience = formData.audience || formData.video_type || entity.audience || entity.video_type || 'teacher_training';
      const activityId = entity.activity_id || entity.month_id;

      if (resourceType !== 'video' && formData.file) {
        const fd = new FormData();
        fd.append('activity_id', activityId);
        fd.append('title', title);
        fd.append('description', description);
        fd.append('resource_type', resourceType);
        fd.append('audience', audience);
        fd.append('file', formData.file);
        editMutationMap.resource.mutate({ id: entity.id, payload: fd });
        return;
      }
      if (resourceType !== 'video') {
        editMutationMap.resource.mutate({
          id: entity.id,
          payload: {
            title,
            description,
            resource_type: resourceType,
            audience,
            activity_id: activityId,
          },
        });
        return;
      }
      editMutationMap.resource.mutate({
        id: entity.id,
        payload: {
          title,
          description,
          resource_type: 'video',
          youtube_url: formData.youtube_url ?? entity.youtube_url ?? entity.source_url ?? '',
          audience,
          activity_id: activityId,
        },
      });
      return;
    }

    const payload = {
      name: formData.name ?? entity.name ?? entity.title ?? '',
      description,
    };
    if (type === 'course') payload.program_id = entity.program_id || defaultProgramId;
    if (type === 'module') payload.course_id = entity.course_id;
    if (type === 'month') payload.module_id = entity.module_id;

    editMutationMap[type].mutate({ id: entity.id, payload });
  };

  const matchesSearch = (item) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    const name = (item.name || item.title || '').toLowerCase();
    const description = (item.description || '').toLowerCase();
    return name.includes(query) || description.includes(query);
  };

  // Render Structure Tree (Courses > Modules > Activities > Videos; Program hidden)
  const renderStructureTree = () => {
    const filteredCourses = allCourses.filter(matchesSearch);

    return (
      <List>
        {filteredCourses.length === 0 ? (
          <ListItem>
            <ListItemText primary="No courses found" />
          </ListItem>
        ) : (
          filteredCourses.map((course) => {
            const courseId = `course-${course.id}`;
            const courseExpanded = expandedItems.has(courseId);
            const courseSelected = selectedItem?.id === course.id && selectedItem?.type === 'course';
            const courseModules = (modules[course.id] || []).filter(matchesSearch);
            const courseHasChildren = courseModules.length > 0;

            return (
              <Box key={course.id}>
                <ListItemButton
                  onClick={() => handleSelectItem(course, 'course')}
                  selected={courseSelected}
                  sx={{
                    pl: 2,
                    backgroundColor: courseSelected ? 'primary.light' : 'transparent',
                    borderLeft: courseSelected ? '4px solid' : '4px solid transparent',
                    borderColor: courseSelected ? 'primary.main' : 'transparent',
                    '&:hover': {
                      backgroundColor: courseSelected ? 'primary.light' : 'action.hover',
                    },
                    fontWeight: courseSelected ? 'bold' : 'normal',
                    color: courseSelected ? 'primary.dark' : 'text.primary',
                    cursor: 'pointer',
                  }}
                >
                  {courseHasChildren && (
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggleExpand(courseId);
                      }}
                      sx={{ mr: 0.5 }}
                    >
                      {courseExpanded ? <ExpandLess /> : <ExpandMore />}
                    </IconButton>
                  )}
                  {!courseHasChildren && <Box sx={{ width: 32 }} />}
                  <ListItemIcon sx={{ minWidth: 36, color: courseSelected ? 'primary.main' : 'inherit' }}>
                    <MenuBook />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 'inherit', color: 'inherit' }}>
                          {course.name}
                        </Typography>
                        {courseHasChildren && (
                          <Badge badgeContent={courseModules.length} color="primary" sx={{ '& .MuiBadge-badge': { fontSize: '0.7rem' } }} />
                        )}
                      </Box>
                    }
                  />
                </ListItemButton>
                <Collapse in={courseExpanded} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {courseModules.map((mod) => {
                      const modId = `structure-module-${mod.id}`;
                      const modExpanded = expandedItems.has(modId);
                      const modSelected = selectedItem?.id === mod.id && selectedItem?.type === 'module';
                      const moduleMonths = (months[mod.id] || []).filter(matchesSearch);
                      const modHasChildren = moduleMonths.length > 0;

                      return (
                        <Box key={mod.id}>
                          <ListItemButton
                            onClick={() => handleSelectItem(mod, 'module')}
                            selected={modSelected}
                            sx={{
                              pl: 4,
                              backgroundColor: modSelected ? 'primary.light' : 'transparent',
                              borderLeft: modSelected ? '4px solid' : '4px solid transparent',
                              borderColor: modSelected ? 'primary.main' : 'transparent',
                              '&:hover': {
                                backgroundColor: modSelected ? 'primary.light' : 'action.hover',
                              },
                              fontWeight: modSelected ? 'bold' : 'normal',
                              color: modSelected ? 'primary.dark' : 'text.primary',
                              cursor: 'pointer',
                            }}
                          >
                            {modHasChildren && (
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleToggleExpand(modId);
                                }}
                                sx={{ mr: 0.5 }}
                              >
                                {modExpanded ? <ExpandLess /> : <ExpandMore />}
                              </IconButton>
                            )}
                            {!modHasChildren && <Box sx={{ width: 32 }} />}
                            <ListItemIcon sx={{ minWidth: 36, color: modSelected ? 'primary.main' : 'inherit' }}>
                              <Inventory />
                            </ListItemIcon>
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 'inherit', color: 'inherit' }}>
                                    {mod.name}
                                  </Typography>
                                  {modHasChildren && (
                                    <Badge badgeContent={moduleMonths.length} color="primary" sx={{ '& .MuiBadge-badge': { fontSize: '0.7rem' } }} />
                                  )}
                                </Box>
                              }
                            />
                          </ListItemButton>
                          <Collapse in={modExpanded} timeout="auto" unmountOnExit>
                            <List component="div" disablePadding>
                              {moduleMonths.map((month) => {
                                const monthId = `structure-month-${month.id}`;
                                const monthExpanded = expandedItems.has(monthId);
                                const monthSelected = selectedItem?.id === month.id && selectedItem?.type === 'month';
                                const monthVideos = (videos[month.id] || []).filter(matchesSearch);
                                const monthHasChildren = monthVideos.length > 0;

                                return (
                                  <Box key={month.id}>
                                    <ListItemButton
                                      onClick={() => handleSelectItem(month, 'month')}
                                      selected={monthSelected}
                                      sx={{
                                        pl: 6,
                                        backgroundColor: monthSelected ? 'primary.light' : 'transparent',
                                        borderLeft: monthSelected ? '4px solid' : '4px solid transparent',
                                        borderColor: monthSelected ? 'primary.main' : 'transparent',
                                        '&:hover': {
                                          backgroundColor: monthSelected ? 'primary.light' : 'action.hover',
                                        },
                                        fontWeight: monthSelected ? 'bold' : 'normal',
                                        color: monthSelected ? 'primary.dark' : 'text.primary',
                                        cursor: 'pointer',
                                      }}
                                    >
                                      {monthHasChildren && (
                                        <IconButton
                                          size="small"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleToggleExpand(monthId);
                                          }}
                                          sx={{ mr: 0.5 }}
                                        >
                                          {monthExpanded ? <ExpandLess /> : <ExpandMore />}
                                        </IconButton>
                                      )}
                                      {!monthHasChildren && <Box sx={{ width: 32 }} />}
                                      <ListItemIcon sx={{ minWidth: 36, color: monthSelected ? 'primary.main' : 'inherit' }}>
                                        <CalendarToday />
                                      </ListItemIcon>
                                      <ListItemText
                                        primary={
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography variant="body2" sx={{ fontWeight: 'inherit', color: 'inherit' }}>
                                              {month.name}
                                            </Typography>
                                            {monthHasChildren && (
                                              <Badge badgeContent={monthVideos.length} color="secondary" sx={{ '& .MuiBadge-badge': { fontSize: '0.7rem' } }} />
                                            )}
                                          </Box>
                                        }
                                      />
                                    </ListItemButton>
                                    <Collapse in={monthExpanded} timeout="auto" unmountOnExit>
                                      <List component="div" disablePadding>
                                        {monthVideos.map((video) => {
                                          const videoSelected = selectedItem?.id === video.id && selectedItem?.type === 'resource';
                                          return (
                                            <ListItemButton
                                              key={video.id}
                                              onClick={() => handleSelectItem(video, 'resource')}
                                              selected={videoSelected}
                                              sx={{
                                                pl: 8,
                                                backgroundColor: videoSelected ? 'primary.light' : 'transparent',
                                                borderLeft: videoSelected ? '4px solid' : '4px solid transparent',
                                                borderColor: videoSelected ? 'primary.main' : 'transparent',
                                                '&:hover': {
                                                  backgroundColor: videoSelected ? 'primary.light' : 'action.hover',
                                                },
                                                fontWeight: videoSelected ? 'bold' : 'normal',
                                                color: videoSelected ? 'primary.dark' : 'text.primary',
                                                cursor: 'pointer',
                                              }}
                                            >
                                              <Box sx={{ width: 32 }} />
                                              <ListItemIcon sx={{ minWidth: 36, color: videoSelected ? 'primary.main' : 'inherit' }}>
                                                <VideoLibrary />
                                              </ListItemIcon>
                                              <ListItemText
                                                primary={
                                                  <Typography variant="body2" sx={{ fontWeight: 'inherit', color: 'inherit' }}>
                                                    {video.title}
                                                  </Typography>
                                                }
                                              />
                                            </ListItemButton>
                                          );
                                        })}
                                      </List>
                                    </Collapse>
                                  </Box>
                                );
                              })}
                            </List>
                          </Collapse>
                        </Box>
                      );
                    })}
                  </List>
                </Collapse>
              </Box>
            );
          })
        )}
      </List>
    );
  };

  // Render Content Tree (Modules > Activities > Videos)
  const renderContentTree = () => {
    const allModules = Object.values(modules).flat();

    const renderModule = (mod) => {
      const modId = `content-module-${mod.id}`;
      const isExpanded = expandedItems.has(modId);
      const isSelected = selectedItem?.id === mod.id && selectedItem?.type === 'module';
      const moduleMonths = (months[mod.id] || []).filter(matchesSearch);
      const hasChildren = moduleMonths.length > 0;

      const course = allCourses.find((c) => modules[c.id]?.some((m) => m.id === mod.id));

      return (
        <Box key={mod.id}>
          <ListItemButton
            onClick={() => handleSelectItem(mod, 'module')}
            selected={isSelected}
            sx={{
              pl: 2,
              backgroundColor: isSelected ? 'primary.light' : 'transparent',
              borderLeft: isSelected ? '4px solid' : '4px solid transparent',
              borderColor: isSelected ? 'primary.main' : 'transparent',
              '&:hover': {
                backgroundColor: isSelected ? 'primary.light' : 'action.hover',
              },
              fontWeight: isSelected ? 'bold' : 'normal',
              color: isSelected ? 'primary.dark' : 'text.primary',
            }}
          >
            {hasChildren && (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleToggleExpand(modId);
                }}
                sx={{ mr: 0.5 }}
              >
                {isExpanded ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            )}
            {!hasChildren && <Box sx={{ width: 32 }} />}
            <ListItemIcon sx={{ minWidth: 36, color: isSelected ? 'primary.main' : 'inherit' }}>
              <Inventory />
            </ListItemIcon>
            <ListItemText
              primary={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 'inherit', color: 'inherit' }}>
                    {mod.name}
                  </Typography>
                  {course && (
                    <Typography variant="caption" color="text.secondary">
                      {course.name}
                    </Typography>
                  )}
                </Box>
              }
              secondary={
                hasChildren && (
                  <Badge badgeContent={moduleMonths.length} color="secondary" sx={{ '& .MuiBadge-badge': { fontSize: '0.7rem' } }} />
                )
              }
            />
          </ListItemButton>
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {moduleMonths.map((month) => {
                const monthId = `month-${month.id}`;
                const monthExpanded = expandedItems.has(monthId);
                const monthSelected = selectedItem?.id === month.id && selectedItem?.type === 'month';
                const monthVideos = (videos[month.id] || []).filter(matchesSearch);
                const monthHasChildren = monthVideos.length > 0;

                return (
                  <Box key={month.id}>
                    <ListItemButton
                      onClick={() => handleSelectItem(month, 'month')}
                      selected={monthSelected}
                      sx={{
                        pl: 4,
                        backgroundColor: monthSelected ? 'primary.light' : 'transparent',
                        borderLeft: monthSelected ? '4px solid' : '4px solid transparent',
                        borderColor: monthSelected ? 'primary.main' : 'transparent',
                        '&:hover': {
                          backgroundColor: monthSelected ? 'primary.light' : 'action.hover',
                        },
                        fontWeight: monthSelected ? 'bold' : 'normal',
                        color: monthSelected ? 'primary.dark' : 'text.primary',
                        cursor: 'pointer',
                      }}
                    >
                      {monthHasChildren && (
                        <IconButton
                  size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggleExpand(monthId);
                          }}
                          sx={{ mr: 0.5 }}
                        >
                          {monthExpanded ? <ExpandLess /> : <ExpandMore />}
                        </IconButton>
                      )}
                      {!monthHasChildren && <Box sx={{ width: 32 }} />}
                      <ListItemIcon sx={{ minWidth: 36, color: monthSelected ? 'primary.main' : 'inherit' }}>
                        <CalendarToday />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 'inherit', color: 'inherit' }}>
                              {month.name}
                            </Typography>
                            {monthHasChildren && (
                              <Badge badgeContent={monthVideos.length} color="secondary" sx={{ '& .MuiBadge-badge': { fontSize: '0.7rem' } }} />
                            )}
                          </Box>
                        }
                      />
                    </ListItemButton>
                    <Collapse in={monthExpanded} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding>
                        {monthVideos.map((video) => {
                          const videoSelected = selectedItem?.id === video.id && selectedItem?.type === 'resource';

                          return (
                            <ListItemButton
                              key={video.id}
                              onClick={() => handleSelectItem(video, 'resource')}
                              selected={videoSelected}
                              sx={{
                                pl: 6,
                                backgroundColor: videoSelected ? 'primary.light' : 'transparent',
                                borderLeft: videoSelected ? '4px solid' : '4px solid transparent',
                                borderColor: videoSelected ? 'primary.main' : 'transparent',
                                '&:hover': {
                                  backgroundColor: videoSelected ? 'primary.light' : 'action.hover',
                                },
                                fontWeight: videoSelected ? 'bold' : 'normal',
                                color: videoSelected ? 'primary.dark' : 'text.primary',
                                cursor: 'pointer',
                              }}
                            >
                              <Box sx={{ width: 32 }} />
                              <ListItemIcon sx={{ minWidth: 36, color: videoSelected ? 'primary.main' : 'inherit' }}>
                                <VideoLibrary />
                              </ListItemIcon>
                              <ListItemText
                                primary={
                                  <Typography variant="body2" sx={{ fontWeight: 'inherit', color: 'inherit' }}>
                                    {video.title}
                                  </Typography>
                                }
                              />
                            </ListItemButton>
                          );
                        })}
                      </List>
                    </Collapse>
                  </Box>
                );
              })}
            </List>
          </Collapse>
    </Box>
  );
};

    const filteredModules = allModules.filter((mod) => {
      const hasMatchingMonths = (months[mod.id] || []).some(matchesSearch);
      return matchesSearch(mod) || hasMatchingMonths;
    });

    return (
      <List>
        {filteredModules.length === 0 ? (
          <ListItem>
            <ListItemText primary="No modules with content found" />
          </ListItem>
        ) : (
          filteredModules.map(renderModule)
        )}
      </List>
    );
  };

  const getSelectedItemChildren = () => {
    if (!selectedItem) return [];
    const { type, id } = selectedItem;
    if (type === 'course') return modules[id] || [];
    if (type === 'module') return months[id] || [];
    if (type === 'month') return resources[id] || [];
    return [];
  };

  const selectedChildren = getSelectedItemChildren();
  const nextChildType = selectedItem
    ? (activeTab === 0
      ? { course: 'module', module: 'month', month: 'resource' }
      : { module: 'month', month: 'resource' })[selectedItem.type]
    : null;

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setSelectedItem(null); // Clear selection when switching tabs
  };

  const parentItem = selectedItem
    ? findParentItem(selectedItem, { allCourses, modules, months })
    : null;

  const handleBackOneStep = () => {
    if (!selectedItem) return;
    if (parentItem) {
      setSelectedItem(parentItem);
      return;
    }
    setSelectedItem(null);
  };

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 64px)' }}>
      {/* Left Sidebar */}
      <Drawer
        variant="permanent"
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
            position: 'relative',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={handleTabChange} variant="fullWidth">
            <Tab icon={<Layers />} label="Structure" iconPosition="start" />
            <Tab icon={<PlayArrow />} label="Content" iconPosition="start" />
          </Tabs>
        </Box>

        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                <TextField
                  size="small"
                  fullWidth
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: <Search sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />,
            }}
          />
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  fullWidth
                  sx={{ mt: 2 }}
                  onClick={() => {
                    if (activeTab === 0) {
                      handleCreate('course', defaultProgramId);
                    } else {
                      toast.info('Select a module first to add activities');
                    }
                  }}
                >
                  {activeTab === 0 ? 'New Course' : 'New Activity'}
                </Button>
        </Box>

        <Box sx={{ overflow: 'auto', flex: 1 }}>
        {loadingCourses ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <CircularProgress />
          </Box>
          ) : activeTab === 0 ? (
            renderStructureTree()
          ) : (
            renderContentTree()
          )}
                    </Box>
      </Drawer>

      {/* Main Content Area */}
      <Box sx={{ flexGrow: 1, overflow: 'auto', p: 3 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
          {selectedItem ? (
            <Button startIcon={<ArrowBack />} onClick={handleBackOneStep}>
              {getHierarchyBackLabel(parentItem)}
            </Button>
          ) : (
            <PageBackButton />
          )}
        </Stack>
        {!selectedItem ? (
                        <Box>
            <Typography variant="h4" gutterBottom>
              {activeTab === 0 ? 'Structure Management' : 'Content Management'}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              {activeTab === 0
                ? 'Manage the structure: Courses, Modules, and Activities. Select an item from the left to view details and manage it.'
                : 'Manage content: Activities and Learning Resources. Select a module or activity from the left to view and manage its content.'}
            </Typography>
                        </Box>
        ) : (
                            <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Box>
                <Typography variant="h4">{selectedItem.name || selectedItem.title}</Typography>
                <Chip label={typeLabel(selectedItem.type)} size="small" sx={{ mt: 1 }} />
                                </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button variant="outlined" startIcon={<Edit />} onClick={() => handleEdit(selectedItem, selectedItem.type)}>
                  Edit
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<Delete />}
                  onClick={() => handleDelete(selectedItem.id, selectedItem.type)}
                >
                  Delete
                </Button>
                {nextChildType && (
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => handleCreate(nextChildType, selectedItem.id)}
                  >
                    Add {typeLabel(nextChildType)}
                  </Button>
                )}
                          </Box>
                      </Box>

            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                Description
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedItem.description || 'No description provided'}
              </Typography>
            </Paper>

            {selectedChildren.length > 0 && (
              <Paper>
                <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="h6">
                    {typeLabel(nextChildType)}s ({selectedChildren.length})
                  </Typography>
                  <Chip
                    label="Click row to view details"
                    size="small"
                    color="primary"
                    variant="outlined"
                    sx={{ fontSize: '0.75rem' }}
                  />
                  </Box>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedChildren.map((child) => (
                        <TableRow
                          key={child.id}
                          hover
                          sx={{
                            cursor: 'pointer',
                            '&:hover': {
                              backgroundColor: 'action.hover',
                              transform: 'scale(1.01)',
                              transition: 'all 0.2s ease-in-out',
                            },
                            '& td': {
                              borderBottom: '1px solid rgba(224, 224, 224, 1)',
                            },
                          }}
                          onClick={() => handleSelectItem(child, nextChildType)}
                        >
                          <TableCell sx={{ fontWeight: 500 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {nextChildType === 'course' && <MenuBook fontSize="small" color="action" />}
                              {nextChildType === 'module' && <Inventory fontSize="small" color="action" />}
                              {(nextChildType === 'month' || nextChildType === 'activity') && <CalendarToday fontSize="small" color="action" />}
                              {nextChildType === 'resource' && <VideoLibrary fontSize="small" color="action" />}
                              <Typography variant="body2" sx={{ fontWeight: 'inherit' }}>
                                {child.name || child.title}
                              </Typography>
              </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 400 }}>
                              {child.description || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEdit(child, nextChildType);
                              }}
                              sx={{
                                '&:hover': {
                                  backgroundColor: 'primary.light',
                                  color: 'primary.main',
                                },
                              }}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(child.id, nextChildType);
                              }}
                              sx={{
                                '&:hover': {
                                  backgroundColor: 'error.light',
                                },
                              }}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            )}

            {selectedChildren.length === 0 && nextChildType && (
              <Paper sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  No {typeLabel(nextChildType)}s yet
                </Typography>
                <Button variant="contained" startIcon={<Add />} onClick={() => handleCreate(nextChildType, selectedItem.id)}>
                  Add {typeLabel(nextChildType)}
                </Button>
              </Paper>
            )}

            {selectedItem.type === 'resource' && (
              <Paper sx={{ p: 2, mt: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Resource Details
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Resource type:</strong> {selectedItem.resource_type || 'video'}
                </Typography>
                {(selectedItem.resource_type || 'video') === 'video' ? (
                  <>
                    <Box sx={{ mb: 2 }}>
                      <Button
                        variant="contained"
                        startIcon={<PlayArrow />}
                        onClick={() => {
                          setVideoUrl(selectedItem.youtube_url || selectedItem.source_url);
                          setVideoPlayerOpen(true);
                        }}
                        sx={{ mb: 2 }}
                      >
                        {getEmbeddableUrl(selectedItem.youtube_url || selectedItem.source_url)?.kind === 'web'
                          ? 'Open link'
                          : 'Play Video'}
                      </Button>
                    </Box>
                    <Typography variant="body2" gutterBottom>
                      <strong>Link:</strong>{' '}
                      <Link href={selectedItem.youtube_url || selectedItem.source_url} target="_blank" rel="noopener noreferrer">
                        {selectedItem.youtube_url || selectedItem.source_url}
                      </Link>
                    </Typography>
                  </>
                ) : (
                  <Typography variant="body2" gutterBottom>
                    <strong>File:</strong>{' '}
                    <Button
                      size="small"
                      variant="text"
                      disabled={fileViewerLoading}
                      onClick={() => openResourceFile(selectedItem)}
                      sx={{ textTransform: 'none', p: 0, minWidth: 0, verticalAlign: 'baseline' }}
                    >
                      {fileViewerLoading ? 'Opening…' : 'Open file'}
                    </Button>
                  </Typography>
                )}
                <Typography variant="body2">
                  <strong>Audience:</strong> {selectedItem.audience || selectedItem.video_type || 'teacher_training'}
                </Typography>
              </Paper>
            )}
    </Box>
        )}
      </Box>

      {/* Create Dialog */}
      <Dialog open={createDialog.open} onClose={() => setCreateDialog({ open: false, type: '', parentId: null })} maxWidth="sm" fullWidth>
        <DialogTitle>
          Create {typeLabel(createDialog.type)}
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            label={createDialog.type === 'resource' ? 'Title' : 'Name'}
            fullWidth
            size="small"
            value={
              createDialog.type === 'resource'
                ? (formData.title ?? '')
                : (formData.name ?? '')
            }
            onChange={(e) => {
              const value = e.target.value;
              if (createDialog.type === 'resource') {
                setFormData({ ...formData, title: value });
              } else {
                setFormData({ ...formData, name: value });
              }
            }}
            required
          />
          <TextField
            label="Description"
            fullWidth
            size="small"
            multiline
            rows={3}
            value={formData.description || ''}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          />
          {createDialog.type === 'resource' && (
            <>
              <TextField
                select
                label="Resource Type"
                fullWidth
                size="small"
                value={formData.resource_type || 'video'}
                onChange={(e) => setFormData({ ...formData, resource_type: e.target.value, file: null })}
              >
                {RESOURCE_TYPE_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </TextField>
              {(formData.resource_type || 'video') === 'video' ? (
                <TextField
                  label="YouTube or website URL"
                  fullWidth
                  size="small"
                  placeholder="https://www.youtube.com/watch?v=... or https://..."
                  value={formData.youtube_url || ''}
                  onChange={(e) => setFormData({ ...formData, youtube_url: e.target.value })}
                  required
                />
              ) : (
                <Button variant="outlined" component="label" fullWidth>
                  {formData.file ? formData.file.name : 'Upload file'}
                  <input
                    type="file"
                    hidden
                    accept={
                      formData.resource_type === 'pdf'
                        ? '.pdf'
                        : formData.resource_type === 'worksheet'
                          ? '.pdf,.png,.jpg,.jpeg,.webp'
                          : '.png,.jpg,.jpeg,.webp,.gif'
                    }
                    onChange={(e) => setFormData({ ...formData, file: e.target.files?.[0] || null })}
                  />
                </Button>
              )}
              <TextField
                select
                label="Audience"
                fullWidth
                size="small"
                value={formData.audience || formData.video_type || 'teacher_training'}
                onChange={(e) => setFormData({ ...formData, audience: e.target.value, video_type: e.target.value })}
              >
                <MenuItem value="teacher_training">Teacher Training</MenuItem>
                <MenuItem value="student_teaching">Student Teaching</MenuItem>
              </TextField>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialog({ open: false, type: '', parentId: null })}>Cancel</Button>
          <Button onClick={handleCreateSubmit} variant="contained" disabled={createMutations[createDialog.type]?.isLoading}>
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialog.open} onClose={() => setEditDialog({ open: false, type: '', entity: null })} maxWidth="sm" fullWidth>
        <DialogTitle>
          Edit {typeLabel(editDialog.type)}
        </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
        <TextField
            label={editDialog.type === 'resource' ? 'Title' : 'Name'}
          fullWidth
          size="small"
            value={
              editDialog.type === 'resource'
                ? (formData.title ?? editDialog.entity?.title ?? editDialog.entity?.name ?? '')
                : (formData.name ?? editDialog.entity?.name ?? editDialog.entity?.title ?? '')
            }
            onChange={(e) => {
              const value = e.target.value;
              if (editDialog.type === 'resource') {
                setFormData({ ...formData, title: value });
              } else {
                setFormData({ ...formData, name: value });
              }
            }}
        />
        <TextField
          label="Description"
          fullWidth
          size="small"
            multiline
            rows={3}
            value={formData.description !== undefined ? formData.description : editDialog.entity?.description || ''}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          />
          {editDialog.type === 'resource' && (
          <>
            {(editDialog.entity?.resource_type || 'video') === 'video' ? (
              <TextField
                label="YouTube or website URL"
                fullWidth
                size="small"
                placeholder="https://www.youtube.com/watch?v=... or https://..."
                value={formData.youtube_url !== undefined ? formData.youtube_url : editDialog.entity?.youtube_url || editDialog.entity?.source_url || ''}
                onChange={(e) => setFormData({ ...formData, youtube_url: e.target.value })}
              />
            ) : (
              <Button variant="outlined" component="label" fullWidth>
                {formData.file ? formData.file.name : 'Replace file (optional)'}
                <input
                  type="file"
                  hidden
                  onChange={(e) => setFormData({ ...formData, file: e.target.files?.[0] || null })}
                />
              </Button>
            )}
            <TextField
              select
              label="Audience"
              fullWidth
              size="small"
                value={formData.audience || formData.video_type || editDialog.entity?.audience || editDialog.entity?.video_type || 'teacher_training'}
                onChange={(e) => setFormData({ ...formData, audience: e.target.value, video_type: e.target.value })}
            >
              <MenuItem value="teacher_training">Teacher Training</MenuItem>
              <MenuItem value="student_teaching">Student Teaching</MenuItem>
            </TextField>
          </>
        )}
      </DialogContent>
      <DialogActions>
          <Button onClick={() => setEditDialog({ open: false, type: '', entity: null })}>Cancel</Button>
          <Button onClick={handleEditSubmit} variant="contained" disabled={editMutationMap[editDialog.type]?.isLoading}>
          Save
        </Button>
      </DialogActions>
    </Dialog>

      {/* Video Player Dialog */}
      <Dialog
        open={videoPlayerOpen}
        onClose={() => setVideoPlayerOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { height: '90vh' }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6">
              {getEmbeddableUrl(videoUrl)?.kind === 'web' ? 'Website' : 'Video Player'}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {getEmbeddableUrl(videoUrl)?.openUrl && (
                <Button
                  component="a"
                  href={getEmbeddableUrl(videoUrl).openUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  size="small"
                  sx={{ mr: 1 }}
                >
                  Open in new tab
                </Button>
              )}
              <IconButton onClick={() => setVideoPlayerOpen(false)} size="small">
                <Close />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0, display: 'flex', justifyContent: 'center', alignItems: 'center', backgroundColor: getEmbeddableUrl(videoUrl)?.kind === 'web' ? '#fff' : 'black' }}>
          <LmsEmbedFrame url={videoUrl} />
        </DialogContent>
      </Dialog>

      <Dialog open={!!fileViewer} onClose={closeFileViewer} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">{fileViewer?.title || 'File'}</Typography>
            <IconButton onClick={closeFileViewer} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0, minHeight: 480 }}>
          {fileViewer?.isImage ? (
            <Box
              component="img"
              src={fileViewer.url}
              alt={fileViewer.title || 'File'}
              sx={{ display: 'block', maxWidth: '100%', mx: 'auto' }}
            />
          ) : fileViewer?.url ? (
            <Box
              component="iframe"
              src={fileViewer.url}
              title={fileViewer.title || 'File'}
              sx={{ width: '100%', minHeight: 640, border: 'none' }}
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default LMSContent;
